import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getOnboarding, publishOnboarding, type OnboardingStatus } from '../api/endpoints/onboarding';
import { createService, listServices } from '../api/endpoints/services';
import type { BusinessDetail, BusinessServiceRecord } from '../api/schemas';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { PhotoManager } from '../components/PhotoManager';
import { countryOfZone } from '../lib/country';
import { formatMoneyShort } from '../lib/format';
import { reasonFix } from '../lib/onboarding-reasons';
import { bookingLinkUrl, consumerOrigin } from '../lib/reservation-link';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import '../styles/first-day.css';

const ONBOARDING_QUERY_KEY = ['business-onboarding'];
const FIRST_DAY_SERVICES_KEY = ['first-day-services'];
/** The Servicios page reads its own list under this key; a new service has to show there too. */
const SERVICES_PAGE_KEY = ['business-services'];
const DEFAULT_MINUTES = '30';

type NewService = { name: string; durationMin: number; priceCents: number };

/**
 * The first day of a business that only receives reservations (Martín, 1 Nov 2026):
 * photos, services and hours on one page. Since signup leaves it published, it
 * shows in the app on its own as soon as the server's checks pass; this page
 * says what is still missing, in the owner's words, and nothing else.
 */
export function ReservationsFirstDay({ business }: { business: BusinessDetail }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const statusQuery = useQuery({ queryKey: ONBOARDING_QUERY_KEY, queryFn: getOnboarding });
  const servicesQuery = useQuery({ queryKey: FIRST_DAY_SERVICES_KEY, queryFn: () => listServices(false) });
  const publish = useMutationAttempt({
    mutationFn: (_input: null, key: string) => publishOnboarding(key),
    onSuccess: (status: OnboardingStatus) => {
      client.setQueryData(ONBOARDING_QUERY_KEY, status);
    },
  });

  const status = statusQuery.data;
  const inApp = status?.state === 'published' && status.ready;
  const reasons = status?.blockingReasons ?? [];
  const pendingApproval = reasons.includes('pending_approval');
  const missing = reasons.filter((reason) => reason !== 'pending_approval');
  const services = (servicesQuery.data?.data ?? []).filter((service) => service.isActive && service.isTransactional);
  const hasHours = status ? !reasons.includes('hours') : false;
  const appUrl = bookingLinkUrl(consumerOrigin(window.location.hostname, countryOfZone(business.neighborhood)), business.id);

  return (
    <main className="first-day">
      <PageHeader
        title={inApp ? 'Ya sales en la app de Dale' : 'Prepara tu ficha'}
        description={
          inApp
            ? 'Tus clientes ya te encuentran y reservan. Mejora tu ficha cuando quieras.'
            : 'Tus fotos, tus servicios y tu horario. Con eso sales en la app de Dale.'
        }
      />
      {statusQuery.isLoading ? <LoadingState label="Revisando tu ficha…" /> : null}
      {statusQuery.isError ? <ErrorBanner message={message(statusQuery.error)} /> : null}
      {inApp ? (
        <p role="status" className="notice first-day-live">
          <span>Tu ficha está en la app y recibe reservas.</span>
          <a className="btn small secondary" href={appUrl} target="_blank" rel="noopener noreferrer">
            Ver mi ficha en la app
          </a>
        </p>
      ) : null}
      {pendingApproval ? <p role="status" className="notice">{reasonFix('pending_approval', false).copy}</p> : null}

      <ol className="first-day-steps">
        <li className="card first-day-step">
          <div className="first-day-step-head">
            <span className="first-day-step-number" aria-hidden="true">1</span>
            <div>
              <h2 className="first-day-step-title">Tus fotos</h2>
              <p className="row-sub">La primera es la portada de tu ficha. Con fotos de verdad te reservan más.</p>
            </div>
          </div>
          <PhotoManager businessId={business.id} photos={business.photos ?? []} />
        </li>

        <li className="card first-day-step">
          <div className="first-day-step-head">
            <span className="first-day-step-number" aria-hidden="true" data-done={services.length > 0 || undefined}>2</span>
            <div>
              <h2 className="first-day-step-title">Tus servicios</h2>
              <p className="row-sub">Lo que tus clientes pueden reservar, con lo que dura y lo que cuesta.</p>
            </div>
          </div>
          {services.length > 0 ? (
            <ul className="rows first-day-services">
              {services.map((service) => (
                <ServiceRow key={service.id} service={service} />
              ))}
            </ul>
          ) : null}
          <ServiceForm
            first={services.length === 0}
            onCreated={() => {
              void client.invalidateQueries({ queryKey: FIRST_DAY_SERVICES_KEY });
              void client.invalidateQueries({ queryKey: SERVICES_PAGE_KEY });
              void client.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
              void client.invalidateQueries({ queryKey: ['business-detail', business.id] });
            }}
          />
        </li>

        <li className="card first-day-step">
          <div className="first-day-step-head">
            <span className="first-day-step-number" aria-hidden="true" data-done={hasHours || undefined}>3</span>
            <div>
              <h2 className="first-day-step-title">Tu horario</h2>
              <p className="row-sub">{hasHours ? 'Listo. Lo cambias cuando quieras.' : 'Marca los días y las horas en que recibes reservas.'}</p>
            </div>
          </div>
          <div>
            <button type="button" className="btn small secondary" onClick={() => void navigate({ to: '/agenda' })}>
              {hasHours ? 'Cambiar horario' : 'Marcar mi horario'}
            </button>
          </div>
        </li>
      </ol>

      {status && !inApp && missing.length > 0 ? (
        <section className="section" aria-labelledby="first-day-missing">
          <h2 className="section-label" id="first-day-missing">Te falta para salir en la app</h2>
          <div className="card">
            <ul className="rows">
              {missing.map((reason) => {
                const fix = reasonFix(reason, business.category === 'restaurant');
                return (
                  <li key={reason}>
                    <div className="row">
                      <p className="row-sub grow">{fix.copy}</p>
                      {fix.to ? (
                        <button type="button" className="btn small secondary" onClick={() => void navigate({ to: fix.to! })}>
                          Resolver
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      ) : null}

      {/* Signed up before publishing was automatic: one tap puts it in the app. */}
      {status && status.state === 'draft' && status.ready ? (
        <div className="section">
          {publish.isError ? <ErrorBanner message={message(publish.error)} /> : null}
          <button
            type="button"
            className="btn"
            disabled={publish.isPending}
            onClick={() => (publish.uncertain ? publish.retry() : publish.mutate(null))}
          >
            {publish.isPending ? 'Publicando…' : publish.uncertain ? 'Reintentar' : 'Salir en la app'}
          </button>
        </div>
      ) : null}
    </main>
  );
}

function ServiceRow({ service }: { service: BusinessServiceRecord }) {
  return (
    <li>
      <div className="row">
        <p className="row-title grow">{service.name}</p>
        <p className="row-sub">
          {service.durationMin} min · {service.priceCents > 0 ? formatMoneyShort(service.priceCents) : 'Gratis'}
        </p>
      </div>
    </li>
  );
}

/** Dollars as people write them ("25", "12.50", "12,50") to cents; blank is free. Undefined when it is not a price. */
export function priceCentsFrom(raw: string): number | undefined {
  const text = raw.trim().replace(',', '.');
  if (!text) return 0;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return undefined;
  return Math.round(Number(text) * 100);
}

function ServiceForm({ first, onCreated }: { first: boolean; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [price, setPrice] = useState('');
  const [formError, setFormError] = useState<string>();
  const create = useMutationAttempt({
    mutationFn: (input: NewService, key: string) => createService({ ...input, isTransactional: true }, key),
    exito: 'Servicio agregado',
    onSuccess: () => {
      setName('');
      setMinutes(DEFAULT_MINUTES);
      setPrice('');
      onCreated();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (create.uncertain) {
      create.retry();
      return;
    }
    const durationMin = Number(minutes);
    const priceCents = priceCentsFrom(price);
    if (!name.trim()) return setFormError('Escribe el nombre del servicio.');
    if (!Number.isInteger(durationMin) || durationMin < 5) return setFormError('Escribe cuántos minutos dura, 5 o más.');
    if (priceCents === undefined) return setFormError('Escribe el precio en dólares, por ejemplo 25 o 12.50.');
    setFormError(undefined);
    create.mutate({ name: name.trim(), durationMin, priceCents });
  }

  return (
    <form className="first-day-service-form" onSubmit={submit} noValidate>
      <label className="first-day-field first-day-field-name">
        <span>Servicio</span>
        <input value={name} onChange={(event) => setName(event.target.value)} placeholder={first ? 'Ej.: Corte y secado' : 'Otro servicio'} maxLength={80} />
      </label>
      <label className="first-day-field">
        <span>Minutos</span>
        <input inputMode="numeric" value={minutes} onChange={(event) => setMinutes(event.target.value)} />
      </label>
      <label className="first-day-field">
        <span>Precio ($)</span>
        <input inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} placeholder="0" />
      </label>
      <button type="submit" className="btn small" disabled={create.isPending}>
        {create.isPending ? 'Agregando…' : create.uncertain ? 'Reintentar' : 'Agregar servicio'}
      </button>
      {formError ? <p className="first-day-form-error" role="alert">{formError}</p> : null}
      {create.isError ? <ErrorBanner message={message(create.error)} /> : null}
    </form>
  );
}

function message(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
